/**
 * The Changes view (PRD 10.8, Design Review V-13; Phase 30): what moved since the last forecast,
 * and why. It reads one response — `GET /forecast/diff` (Phase 15) — and draws two things from
 * it: the `entities` as a slope chart between the two runs' peaks, threshold crossings
 * emphasised and unchanged rows faded, and the `changes` as the attributed list beneath.
 *
 * The rules that turn the response into the screen live here, shared by the card, the tests and
 * anything else that has to say what the view says (the digest of Phase 36 reads the same list):
 *
 * - **A line is a row of the dimension with a peak in both runs.** Its two ends are the two
 *   runs' own heatmap peaks (`peak_from`, `peak_to`), so an end on the chart is a cell on the
 *   heatmap. A row with no capacity on one side has a peak in hours and no ratio to draw; it goes
 *   on the shelf beneath the chart, hours only, rather than being dropped.
 * - **Emphasis is the band.** A line whose peak changed band (`peak_band_changed`) is the one
 *   V-13 exists to show; a line that changed without crossing is drawn plainly; one that did not
 *   change fades. Three weights, no fourth channel.
 * - **The axis is capped.** Peak allocation on the dev database runs past 800 %, and an axis
 *   that tall puts the three thresholds in its bottom eighth, where a crossing is invisible.
 *   Ends above the cap are pinned to it and marked, and the label states the real figure.
 * - **The list is the API's order**, largest movement first, which for demand is the absolute
 *   hours delta and for a person's capacity the absolute capacity delta. The card re-sorts
 *   nothing; the rule is here so the tests can state it.
 *
 * Pure: no DOM, no I/O.
 */
import { STATUS_STATES } from './palette.js';
import { col } from './table.js';

/** The shared filters this view reads, in the API's names. */
export const CHANGES_PARAM_KEYS = Object.freeze([
  'run_id',
  'compare_run_id',
  'from',
  'weeks',
  'dimension',
  'entity',
]);

/** The dimensions the diff serves: the heatmap's, without project (a project has no capacity). */
export const CHANGES_DIMENSIONS = Object.freeze([
  'company',
  'department',
  'service',
  'office',
  'individual',
]);

/** The dimension the slope chart draws by default: one line per role (PRD 10.8). */
export const SLOPE_DEFAULT_DIMENSION = 'service';

/**
 * The value axis stops here (300 %). Ends above it are pinned and marked; the thresholds stay
 * readable. A constant rather than a setting: it is presentation, not a forecast figure.
 */
export const SLOPE_CAP = 3;

/** How many changes the card names before the rest go to the table. */
export const CHANGES_SHOWN = 10;

/** The minimum vertical distance between two end labels, in pixels. */
export const LABEL_GAP = 13;

/**
 * The cause vocabulary in the API's precedence order (`modules/forecast/diff/attribution.js`
 * CAUSES, mirrored here so the client does not import the engine; the unit test holds them
 * equal). Ties in the list are broken in this order.
 */
export const CAUSES = Object.freeze([
  'new_project',
  'project_closed',
  'schedule_slip',
  'replanned',
  'progress',
  'scope_change',
  'new_deal',
  'deal_won',
  'deal_lost',
  'deal_excluded',
  'stage_change',
  'baseline_change',
  'capacity_change',
  'assumption_change',
  'unknown',
]);

/** The words a cause is shown as (PRD 7.6 names them; the six additions follow the same form). */
export const CAUSE_LABELS = Object.freeze({
  new_project: 'New project',
  project_closed: 'Project closed',
  schedule_slip: 'Schedule slip',
  replanned: 'Replanned',
  progress: 'Progress',
  scope_change: 'Scope change',
  new_deal: 'New deal',
  deal_won: 'Deal won',
  deal_lost: 'Deal lost',
  deal_excluded: 'Deal excluded',
  stage_change: 'Stage change',
  baseline_change: 'Baseline change',
  capacity_change: 'Capacity change',
  assumption_change: 'Assumption change',
  unknown: 'Unknown',
});

/** The three weights a line is drawn in, and the order they are drawn in (faded first). */
export const EMPHASES = Object.freeze(['unchanged', 'changed', 'crossed']);

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const num = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);
const has = (v) => v !== null && v !== undefined;

/**
 * The dimension the slope chart asks the diff for. The plan says one line per role, and the
 * company is one row — a slope chart with one line answers nothing the KPI does not — so the
 * default dimension draws roles. The diff has no project dimension (a project has no capacity
 * to be a ratio of), so that too is drawn as roles. Every other dimension is itself.
 * @param {string} dimension  the shared filter's dimension
 * @returns {{ dimension: string, substituted: 'company' | 'project' | null }}
 */
export function slopeDimension(dimension) {
  if (dimension === 'company' || dimension === 'project')
    return { dimension: SLOPE_DEFAULT_DIMENSION, substituted: dimension };
  if (!CHANGES_DIMENSIONS.includes(dimension))
    return { dimension: SLOPE_DEFAULT_DIMENSION, substituted: null };
  return { dimension, substituted: null };
}

/** The word for why the two runs were compared, from the pair's roles. */
export function compareRoleLabel(pair) {
  const roles = pair?.reference_roles ?? [];
  if (roles.includes('week_prior')) return 'one week prior';
  if (roles.includes('previous')) return 'the previous run';
  return 'chosen in the filter bar';
}

/** The weight a line is drawn in. */
export function emphasisOf(entity) {
  if (entity?.peak_band_changed) return 'crossed';
  if (entity?.changed) return 'changed';
  return 'unchanged';
}

/** The state (colour, icon, label) a band names; null for no band. */
export function bandState(band) {
  return STATUS_STATES.find((s) => s.key === band) ?? null;
}

const endOf = (peak, cap) => {
  if (!peak || !has(peak.utilization)) return null;
  const value = Number(peak.utilization);
  return {
    value,
    drawn: Math.min(value, cap),
    pinned: value > cap,
    band: peak.band ?? null,
    week_start: peak.week_start,
    allocated_hours: round2(num(peak.allocated_hours)),
    capacity_hours: round2(num(peak.capacity_hours)),
  };
};

/**
 * The slope chart's lines and its shelf from a response's `entities`.
 *
 * A line has an end for each run: the peak's utilization (`value`), where it is drawn (`drawn`,
 * capped), whether it was pinned, its band and week and the two figures behind it. An entity
 * with a peak on only one side (it arrived, or left) is a line with one end, drawn as a dot. An
 * entity with no ratio on either side — no capacity in that run — goes on the shelf with its
 * hours. Lines come back in drawing order: unchanged first, changed, then crossed on top.
 *
 * @param {object[]} entities  `GET /forecast/diff` entities
 * @param {{ cap?: number }} [opts]
 * @returns {{ lines: object[], shelf: object[] }}
 */
export function slopeLines(entities, { cap = SLOPE_CAP } = {}) {
  const lines = [];
  const shelf = [];
  for (const e of entities ?? []) {
    const from = endOf(e.peak_from, cap);
    const to = endOf(e.peak_to, cap);
    const base = {
      key: e.entity_id,
      label: e.label,
      emphasis: emphasisOf(e),
      hours_from: round2(num(e.hours_from)),
      hours_to: round2(num(e.hours_to)),
      hours_delta: round2(num(e.hours_delta)),
      capacity_from: round2(num(e.capacity_from)),
      capacity_to: round2(num(e.capacity_to)),
      weeks_changed: Number(e.weeks_changed ?? 0),
      band_crossings: Number(e.band_crossings ?? 0),
      cause: e.cause ?? null,
      cause_entity: e.cause_entity ?? null,
    };
    if (!from && !to) {
      shelf.push({ ...base, peak_from: e.peak_from ?? null, peak_to: e.peak_to ?? null });
      continue;
    }
    lines.push({
      ...base,
      from,
      to,
      arrived: !from && Boolean(to),
      left: Boolean(from) && !to,
      utilization_delta: from && to ? Math.round((to.value - from.value) * 1e4) / 1e4 : null,
    });
  }
  lines.sort(
    (a, b) =>
      EMPHASES.indexOf(a.emphasis) - EMPHASES.indexOf(b.emphasis) ||
      Math.abs(b.hours_delta) - Math.abs(a.hours_delta) ||
      a.label.localeCompare(b.label),
  );
  shelf.sort((a, b) => Math.abs(b.hours_delta) - Math.abs(a.hours_delta));
  return { lines, shelf };
}

/**
 * The top of the value axis: the tallest end, capped, and never so low that the three
 * thresholds have no room above them.
 * @param {object[]} lines  `slopeLines` output
 * @param {{ overloaded_above?: number }} thresholds
 */
export function slopeAxisMax(lines, thresholds, { cap = SLOPE_CAP } = {}) {
  let peak = 0;
  for (const l of lines ?? []) {
    if (l.from) peak = Math.max(peak, l.from.value);
    if (l.to) peak = Math.max(peak, l.to.value);
  }
  const floor = num(thresholds?.overloaded_above ?? 1.15) * 1.25;
  return Math.max(floor, Math.min(peak, cap));
}

/** A label cut to fit beside an axis, with an ellipsis; the full name stays in the hover. */
export function truncateLabel(label, max = 28) {
  const text = String(label ?? '');
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
}

/**
 * Non-overlapping vertical positions for end labels: each item keeps its own `y` where it can,
 * is pushed down where it would sit on the one above, and the whole column is pulled back up
 * where it would run off the bottom. Returns a Map from key to y.
 * @param {{ key: string, y: number }[]} items
 * @param {{ gap?: number, top: number, bottom: number }} bounds
 */
export function placeLabels(items, { gap = LABEL_GAP, top, bottom }) {
  const sorted = [...(items ?? [])].sort((a, b) => a.y - b.y || a.key.localeCompare(b.key));
  const ys = sorted.map((i) => Math.max(top, Math.min(bottom, i.y)));
  for (let i = 1; i < ys.length; i += 1) ys[i] = Math.max(ys[i], ys[i - 1] + gap);
  if (ys.length && ys[ys.length - 1] > bottom) {
    ys[ys.length - 1] = bottom;
    for (let i = ys.length - 2; i >= 0; i -= 1) ys[i] = Math.min(ys[i], ys[i + 1] - gap);
  }
  return new Map(sorted.map((item, i) => [item.key, ys[i]]));
}

// ---------------------------------------------------------------------------------------------
// The list

/** How far a change moved the selection: hours for demand, hours of capacity for a person. */
export function changeMagnitude(c) {
  return Math.max(Math.abs(num(c?.hours_delta)), Math.abs(num(c?.capacity_delta)));
}

const causeRank = (c) => {
  const i = CAUSES.indexOf(c);
  return i < 0 ? CAUSES.length : i;
};

/**
 * The list's order — the API's own, restated: largest movement first, then the larger hours
 * delta, then the cause vocabulary's order, then the label.
 * @param {object[]} changes
 */
export function sortChanges(changes) {
  return [...(changes ?? [])].sort(
    (a, b) =>
      changeMagnitude(b) - changeMagnitude(a) ||
      Math.abs(num(b.hours_delta)) - Math.abs(num(a.hours_delta)) ||
      causeRank(a.cause) - causeRank(b.cause) ||
      String(a.label).localeCompare(String(b.label)),
  );
}

/** The word for a cause. */
export function causeLabel(cause) {
  return CAUSE_LABELS[cause] ?? CAUSE_LABELS.unknown;
}

const humanizeField = (field) =>
  String(field ?? '')
    .replace(/_/g, ' ')
    .replace(/^close date adjusted$/, 'close date')
    .replace(/^expected kickoff date$/, 'kickoff')
    .replace(/^window start$/, 'window start')
    .replace(/^window end$/, 'window end')
    .replace(/^slipped from$/, 're-anchored from');

const arrow = ' → ';

/**
 * The evidence behind a change as one sentence, from the detail the attribution stored (the
 * `kind` names the rule that matched). Formatters are injected so the server and the client
 * say numbers the same way; the defaults are plain.
 *
 * @param {{ cause: string, entity_type: string, detail?: object, withheld?: boolean, hours_from?: number, hours_to?: number }} change
 * @param {{ hours?: (h: number) => string, date?: (iso: string) => string, percent?: (r: number) => string }} [fmt]
 */
export function causeSentence(change, fmt = {}) {
  const hours = fmt.hours ?? ((h) => `${round2(num(h))} h`);
  const date = fmt.date ?? ((iso) => (iso ? String(iso) : '—'));
  const pct = fmt.percent ?? ((r) => `${Math.round(num(r) * 100)}%`);
  const d = change?.detail ?? {};
  const k = d.kind;
  if (change?.withheld) return 'Outside your scope';

  switch (k) {
    case 'new_project':
      return 'Not in the compared run';
    case 'project_closed':
      return d.status
        ? `Closed (${d.status}); its remaining hours left the forecast`
        : 'Left the forecast; its remaining hours with it';
    case 'dates': {
      const parts = [];
      const [s0, e0] = d.window_from ?? [];
      const [s1, e1] = d.window_to ?? [];
      if (s0 !== s1 || e0 !== e1)
        parts.push(`plan window ${date(s0)} – ${date(e0)}${arrow}${date(s1)} – ${date(e1)}`);
      if (d.slipped_from_from !== d.slipped_from_to) {
        if (d.slipped_from_to && !d.slipped_from_from)
          parts.push(`re-anchored: work planned for ${date(d.slipped_from_to)} moved forward`);
        else if (!d.slipped_from_to) parts.push('no longer re-anchored');
        else
          parts.push(
            `re-anchored from ${date(d.slipped_from_from)}${arrow}${date(d.slipped_from_to)}`,
          );
      }
      if (d.due_from !== d.due_to) parts.push(`due ${date(d.due_from)}${arrow}${date(d.due_to)}`);
      if (Boolean(d.project_past_due_from) !== Boolean(d.project_past_due_to))
        parts.push(d.project_past_due_to ? 'now past due' : 'no longer past due');
      const text = parts.join('; ');
      return text ? text[0].toUpperCase() + text.slice(1) : 'Dates changed';
    }
    case 'behind':
      return `Behind plan: ${hours(d.actual_from)}${arrow}${hours(d.actual_to)} logged, ${hours(d.remaining_to)} remaining re-spread later`;
    case 'progress':
      return `${hours(d.actual_from)}${arrow}${hours(d.actual_to)} logged, ${hours(d.remaining_to)} remaining`;
    case 'planned_hours':
      return `Plan ${hours(d.planned_from)}${arrow}${hours(d.planned_to)}`;
    case 'recurring_baseline':
      return `Trailing baseline ${hours(d.weekly_hours_from)}${arrow}${hours(d.weekly_hours_to)} a week`;
    case 'inclusion':
      return d.included_to
        ? `Now forecast${d.exclusion_reason_from ? ` (was excluded: ${humanizeField(d.exclusion_reason_from)})` : ''}`
        : `No longer forecast${d.exclusion_reason_to ? ` (${humanizeField(d.exclusion_reason_to)})` : ''}`;
    case 'inference': {
      const changed = (d.changed ?? []).map((c) => {
        const scalar = (v) => v === null || v === undefined || typeof v !== 'object';
        return scalar(c.from) && scalar(c.to) && (has(c.from) || has(c.to))
          ? `${humanizeField(c.field)} ${c.from ?? '—'}${arrow}${c.to ?? '—'}`
          : humanizeField(c.field);
      });
      return changed.length ? `Inference changed: ${changed.join(', ')}` : 'Inference changed';
    }
    case 'replanned':
      return d.slipped
        ? 'Nothing about it changed; still re-anchored, and its remaining work is spread from the later planning week'
        : 'Nothing about it changed; its remaining work is spread from the later planning week';
    case 'settings':
      return `Settings changed: ${(d.groups ?? []).join(', ')}`;
    case 'new_deal':
      return 'Not in the compared run';
    case 'left_run':
      if (d.status === 'won') return 'Won; left the pipeline';
      if (d.status === 'lost') return 'Lost; left the pipeline';
      return `Left the pipeline${d.status ? ` (${d.status})` : ''}`;
    case 'linked_project':
      return 'Won: its project now exists, and the deal no longer counts';
    case 'status':
      return `${humanizeField(d.status_from)}${arrow}${humanizeField(d.status_to)}`;
    case 'stage': {
      const from = d.stage_name_from ?? d.stage_from;
      const to = d.stage_name_to ?? d.stage_to;
      return `Stage ${from}${arrow}${to} (${pct(d.probability_from)}${arrow}${pct(d.probability_to)})`;
    }
    case 'probability':
      return `Probability ${pct(d.probability_from)}${arrow}${pct(d.probability_to)}${d.source_to ? ` (${d.source_to})` : ''}`;
    case 'deal_dates': {
      const moved = (d.changed ?? []).map(
        (c) => `${humanizeField(c.field)} ${date(c.from)}${arrow}${date(c.to)}`,
      );
      const text = moved.join('; ');
      return text ? text[0].toUpperCase() + text.slice(1) : 'Dates changed';
    }
    case 'deal_hours': {
      const parts = [`${hours(d.hours_from)}${arrow}${hours(d.hours_to)}`];
      if (has(d.amount_from) && has(d.amount_to) && d.amount_from !== d.amount_to)
        parts.push(`amount ${d.amount_from}${arrow}${d.amount_to}`);
      if (d.scope_tier_from !== d.scope_tier_to)
        parts.push(`tier ${d.scope_tier_from ?? '—'}${arrow}${d.scope_tier_to ?? '—'}`);
      return `Deal hours ${parts.join(', ')}`;
    }
    case 'nonproject_mode':
      return `Non-project mode ${d.nonproject_mode_from}${arrow}${d.nonproject_mode_to}`;
    case 'nonproject_follows_capacity':
      return `Non-project load ${hours(d.hours_from)}${arrow}${hours(d.hours_to)}, following capacity`;
    case 'nonproject_baseline':
      return `Non-project baseline ${hours(d.hours_from)}${arrow}${hours(d.hours_to)} over the compared weeks`;
    case 'capacity':
      return `Capacity ${hours(d.capacity_from)}${arrow}${hours(d.capacity_to)} over ${d.weeks_changed ?? '?'} ${d.weeks_changed === 1 ? 'week' : 'weeks'}`;
    case 'no_rule':
      return 'No rule matched';
    case 'no_facts':
      return 'Neither run kept facts for it';
    default:
      return '';
  }
}

/** `+12.5 h` / `−12.5 h` / `0 h`, for a delta the eye has to read the sign of. */
export function signedHours(value, hours = (h) => `${round2(num(h))} h`) {
  const n = round2(num(value));
  if (n > 0) return `+${hours(n)}`;
  if (n < 0) return `−${hours(Math.abs(n))}`;
  return hours(0);
}

/**
 * The headline figures of a response, for the card's KPIs and the tests.
 * @param {object} data  a `GET /forecast/diff` response
 */
export function changesSummary(data) {
  const t = data?.totals ?? {};
  const entities = data?.entities ?? [];
  const perCause = Object.entries(t.per_cause ?? {})
    .map(([cause, v]) => ({
      cause,
      label: causeLabel(cause),
      rows: Number(v?.rows ?? 0),
      hours_delta: round2(num(v?.hours_delta)),
    }))
    .filter((c) => c.rows > 0)
    .sort(
      (a, b) =>
        Math.abs(b.hours_delta) - Math.abs(a.hours_delta) ||
        b.rows - a.rows ||
        causeRank(a.cause) - causeRank(b.cause),
    );
  return {
    entities: entities.length,
    entities_changed: entities.filter((e) => e.changed).length,
    peaks_crossed: entities.filter((e) => e.peak_band_changed).length,
    cells_changed: Number(t.rows ?? 0),
    band_changed: Number(t.band_changed ?? 0),
    hours_delta: round2(num(t.hours_delta)),
    capacity_delta: round2(num(t.capacity_delta)),
    changes: Number(t.changes ?? 0),
    folded: t.folded ?? { count: 0, hours_delta: 0, capacity_delta: 0 },
    per_cause: perCause,
    leading_cause: perCause[0] ?? null,
    thresholds_changed:
      JSON.stringify(data?.thresholds ?? {}) !== JSON.stringify(data?.thresholds_from ?? {}),
  };
}

// ---------------------------------------------------------------------------------------------
// Exports

/** One row per entity for the table and the CSV: the two ends and what stands between them. */
export function slopeExportRows(entities) {
  return (entities ?? []).map((e) => ({
    entity_id: e.entity_id,
    label: e.label,
    emphasis: emphasisOf(e),
    peak_from_week: e.peak_from?.week_start ?? null,
    peak_from_utilization: e.peak_from?.utilization ?? null,
    peak_from_hours: e.peak_from ? round2(num(e.peak_from.allocated_hours)) : null,
    peak_from_capacity: e.peak_from ? round2(num(e.peak_from.capacity_hours)) : null,
    band_from: e.peak_from?.band ?? null,
    peak_to_week: e.peak_to?.week_start ?? null,
    peak_to_utilization: e.peak_to?.utilization ?? null,
    peak_to_hours: e.peak_to ? round2(num(e.peak_to.allocated_hours)) : null,
    peak_to_capacity: e.peak_to ? round2(num(e.peak_to.capacity_hours)) : null,
    band_to: e.peak_to?.band ?? null,
    peak_band_changed: Boolean(e.peak_band_changed),
    hours_from: round2(num(e.hours_from)),
    hours_to: round2(num(e.hours_to)),
    hours_delta: round2(num(e.hours_delta)),
    capacity_from: round2(num(e.capacity_from)),
    capacity_to: round2(num(e.capacity_to)),
    weeks_changed: Number(e.weeks_changed ?? 0),
    band_crossings: Number(e.band_crossings ?? 0),
    cause: e.cause ?? null,
    cause_label: e.cause ? causeLabel(e.cause) : null,
    cause_entity: e.cause_entity?.label ?? null,
  }));
}

/** One row per change for the table and the CSV, in the list's order, with the evidence said. */
export function changeExportRows(changes, fmt) {
  return sortChanges(changes).map((c, i) => ({
    rank: i + 1,
    entity_type: c.entity_type,
    entity_id: c.entity_id,
    label: c.label,
    withheld: Boolean(c.withheld),
    cause: c.cause,
    cause_label: causeLabel(c.cause),
    evidence: causeSentence(c, fmt),
    hours_delta: round2(num(c.hours_delta)),
    capacity_delta: round2(num(c.capacity_delta)),
    weeks: Number(c.weeks ?? 0),
    rows: Number(c.rows ?? 0),
    cells: Number(c.cells ?? 0),
    hours_from: c.hours_from ?? null,
    hours_to: c.hours_to ?? null,
    total_hours_delta: c.total_hours_delta ?? null,
    total_capacity_delta: c.total_capacity_delta ?? null,
    weeks_changed: c.weeks_changed ?? null,
  }));
}

/** One row per changed cell for the table and the CSV. */
export function cellExportRows(rows) {
  return (rows ?? []).map((r) => ({
    entity_id: r.entity_id,
    label: r.label,
    week_start: r.week_start,
    hours_from: round2(num(r.hours_from)),
    hours_to: round2(num(r.hours_to)),
    hours_delta: round2(num(r.hours_delta)),
    capacity_from: round2(num(r.capacity_from)),
    capacity_to: round2(num(r.capacity_to)),
    capacity_delta: round2(num(r.capacity_delta)),
    utilization_from: r.utilization_from ?? null,
    utilization_to: r.utilization_to ?? null,
    utilization_delta: r.utilization_delta ?? null,
    band_from: r.band_from ?? null,
    band_to: r.band_to ?? null,
    band_changed: Boolean(r.band_changed),
    cause: r.cause,
    cause_label: causeLabel(r.cause),
    cause_entity: r.cause_entity?.label ?? null,
    contributor_count: Number(r.contributor_count ?? 0),
  }));
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// Specifications, not renderings. The dimension decides what a row is called, so each takes it.

/** The noun a dimension's rows go by, singular then plural. */
export const CHANGE_NOUNS = Object.freeze({
  service: ['role', 'roles'],
  department: ['department', 'departments'],
  office: ['office', 'offices'],
  individual: ['person', 'people'],
  company: ['company', 'company'],
});

/** @param {string} dimension @param {1 | 2} n */
export function changeNoun(dimension, n) {
  return (CHANGE_NOUNS[dimension] ?? ['row', 'rows'])[n === 1 ? 0 : 1];
}

const crossed = (key) => col.text(key, 'Crossed', { csv: (v) => (v ? 'yes' : 'no') });
const band = (key, label) => col.text(key, label, { unit: 'band' });

/** One row per entity: the two peaks the slope chart joins. */
export function slopeColumns({ dimension = 'company' } = {}) {
  return [
    col.text('label', changeNoun(dimension, 1)),
    col.text('emphasis', 'Drawn as'),
    col.week('peak_from_week', 'Peak week then'),
    col.percent('peak_from_utilization', 'Peak then'),
    band('band_from', 'Band then'),
    col.week('peak_to_week', 'Peak week now'),
    col.percent('peak_to_utilization', 'Peak now'),
    band('band_to', 'Band now'),
    crossed('peak_band_changed'),
    col.hours('hours_from', 'Hours then'),
    col.hours('hours_to', 'Hours now'),
    col.hours('hours_delta', 'Change'),
    col.count('weeks_changed', 'Weeks changed'),
    col.count('band_crossings', 'Cells crossing'),
    col.text('cause_label', 'Leading cause'),
    col.text('cause_entity', 'Via'),
  ];
}

/** One row per attributed change, largest first. */
export function changeListColumns({ dimension = 'company' } = {}) {
  return [
    col.count('rank', '#'),
    col.text('label', 'What'),
    col.text('entity_type', 'Kind'),
    col.text('cause_label', 'Cause'),
    col.text('evidence', 'Evidence'),
    col.hours('hours_delta', 'Change in window'),
    col.hours('capacity_delta', 'Capacity change'),
    col.count('weeks', 'Weeks'),
    col.count('rows', changeNoun(dimension, 2)),
    col.count('cells', 'Cells'),
    col.hours('hours_from', 'Hours then'),
    col.hours('hours_to', 'Hours now'),
    col.hours('total_hours_delta', 'Change, every compared week'),
  ];
}

/** One row per changed cell. */
export function changeCellColumns({ dimension = 'company' } = {}) {
  return [
    col.text('label', changeNoun(dimension, 1)),
    col.week('week_start', 'Week'),
    col.hours('hours_from', 'Hours then'),
    col.hours('hours_to', 'Hours now'),
    col.hours('hours_delta', 'Change'),
    col.hours('capacity_delta', 'Capacity change'),
    col.percent('utilization_from', 'Allocation then'),
    col.percent('utilization_to', 'Allocation now'),
    band('band_from', 'Band then'),
    band('band_to', 'Band now'),
    crossed('band_changed'),
    col.text('cause_label', 'Cause'),
    col.text('cause_entity', 'Via'),
    col.count('contributor_count', 'Contributors'),
  ];
}
